import { useState, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useApi, type User, type PageMeta } from '../api';
import { usePermissions } from '../auth';
import UserTable from '../components/users/UserTable';
import UserForm from '../components/users/UserForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Pagination from '../components/common/Pagination';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { PageTitle } from '../components/layout/TitleBar';

export interface UsersPageProps {
  /** Enable pagination (local node). When false, the full list is shown. */
  paginated?: boolean;
  /** Track/show user active status (local node). */
  showStatus?: boolean;
  /** Block deleting this user id (self-delete guard, cloud passes the current user). */
  currentUserId?: string;
  passwordMinLength?: number;
  /** Helper text under the role select in the form. */
  roleNote?: string;
  /** Root container classes. */
  className?: string;
  /** Extra per-row actions (e.g. cloud's "Node Access" button). */
  renderExtraActions?: (user: User) => ReactNode;
}

/**
 * User management — shared by both dashboards. Data flows through useApi().users
 * (each app's adapter normalizes its backend's field names). Node-specific behavior
 * (pagination, status, self-delete guard) comes in via props.
 */
export default function UsersPage({
  paginated = false,
  showStatus = false,
  currentUserId,
  passwordMinLength = 8,
  roleNote,
  className = 'space-y-3',
  renderExtraActions,
}: UsersPageProps) {
  const { users: usersApi } = useApi();
  const { hasPermission } = usePermissions();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Array<{ id: string; name: string }>>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await usersApi.list(paginated ? { page, limit: 20 } : undefined);
      setUsers(Array.isArray(res.data) ? res.data : []);
      setMeta(res.meta ?? null);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }

  async function loadRoles() {
    try {
      const data = await usersApi.listRoles();
      setRoles(Array.isArray(data) ? data : []);
    } catch {
      /* silent */
    }
  }

  async function handleCreate(data: any) {
    await usersApi.create(data);
    setShowForm(false);
    load();
  }

  async function handleUpdate(data: any) {
    if (!editUser) return;
    await usersApi.update(editUser.id, data);
    setEditUser(null);
    load();
  }

  async function handleDelete() {
    if (!deleteUser) return;
    await usersApi.delete(deleteUser.id);
    setDeleteUser(null);
    load();
  }

  /** Reactivate a deactivated user (is_active → true). */
  async function handleActivate(u: User) {
    await usersApi.update(u.id, { is_active: true });
    load();
  }

  async function handleEdit(u: User) {
    try {
      setEditUser(await usersApi.get(u.id));
    } catch {
      setEditUser(u);
    }
    setShowForm(false);
  }

  return (
    <div className={className}>
      {/* Only rendered under the "Users & Roles" tabs page, so it keeps that title. */}
      <PageTitle title="Users & Roles">
        {hasPermission('roles.view') && (
          <Link
            to="/roles"
            className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-3 text-[13px] font-medium text-foreground hover:bg-muted transition-colors"
          >
            <ShieldCheckIcon className="h-4 w-4" />
            Manage Roles
          </Link>
        )}
        {hasPermission('users.create') && (
          <button
            data-tour="users-add"
            onClick={() => { setShowForm(true); setEditUser(null); }}
            className="flex h-8 items-center gap-1.5 rounded-lg bg-brand px-3 text-[13px] font-semibold text-white hover:bg-brand-dark transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add User
          </button>
        )}
      </PageTitle>

      {(showForm || editUser) && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-card">
          <h3 className="mb-4 text-[14px] font-semibold text-foreground">{editUser ? 'Edit User' : 'Create User'}</h3>
          <UserForm
            user={editUser}
            roles={roles}
            onSubmit={editUser ? handleUpdate : handleCreate}
            onCancel={() => { setShowForm(false); setEditUser(null); }}
            showStatus={showStatus}
            passwordMinLength={passwordMinLength}
            roleNote={roleNote}
          />
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <UserTable
          users={users}
          showStatus={showStatus}
          renderExtraActions={renderExtraActions}
          onEdit={hasPermission('users.edit') ? handleEdit : undefined}
          onActivate={hasPermission('users.edit') ? handleActivate : undefined}
          onDelete={
            hasPermission('users.delete')
              ? (u) => {
                  if (currentUserId && u.id === currentUserId) {
                    alert('Cannot delete yourself');
                    return;
                  }
                  setDeleteUser(u);
                }
              : undefined
          }
        />
      )}

      {paginated && <Pagination meta={meta} onPageChange={setPage} />}

      <ConfirmDialog
        open={!!deleteUser}
        title="Delete User"
        message={`Are you sure you want to delete "${deleteUser?.name || deleteUser?.email}"?`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteUser(null)}
      />
    </div>
  );
}
